// ── Serviço do domínio CS (núcleo) ────────────────────────────────
// Orquestra contrato + planilha de vidas. Não conhece Supabase nem DOM —
// tudo passa pela porta ICsRepository.
//
// Regra que atravessa este arquivo: NADA aqui pode derrubar uma venda.
// O registro do contrato acontece depois da RPC de venda já ter sucedido,
// e um erro daqui vira contrato pendente, não venda perdida.

import type { ProposalPlanLine } from '../../plans/ports/IPlansRepository';
import type {
  ClienteImplantacao,
  ClienteImplantacaoResultado,
  Contrato,
  EmpresaParaRota,
  EnvioBloqueado,
  EnvioBloqueadoRegistro,
  GatilhoEmpresa,
  RotaIntegracao,
  RotaVia,
  ContratoDocumento,
  Competencia,
  PreviaBoleto,
  CriarImportacaoInput,
  ICsRepository,
  Importacao,
  RegistrarGatilhoInput,
  UltimaSincronizacaoHub,
  Vida,
} from '../ports/ICsRepository';
import type { ClienteHub, ContaHubDaEmpresa, EmpresaParaVinculo } from '../clientesHub';
import { montarCarteira } from '../carteiraContratos';
import { montarConferenciaBoleto } from '../conferenciaBoleto';
import type { ConferenciaBoleto, ItemDoCatalogo, PreparoBoleto } from '../conferenciaBoleto';
import type { CriarVidaInput, ImportacaoHistorico, VidaRascunho } from '../ports/ICsRepository';
import type { LinhaParaRascunho } from '../csModel';
import { chaveDaLinha, chaveDoErro, validarLinhaDoRascunho } from '../csModel';
import type {
  ContratoDados,
  ContratoModalidade,
  DocumentoStatus,
  EstadoFicha,
  GatilhoTipo,
  PlanilhaLinha,
  PlanilhaValidada,
  PlanoDoCatalogo,
  NegociacaoPlanilha,
  VidaTipo,
} from '../csModel';
import {
  MODALIDADE_PADRAO,
  QUALIFICACAO_LABELS,
  VIDA_MAIS_COBRANCA_PADRAO,
  qualificacaoOuPadrao,
  contratoCamposFaltando,
  documentosNecessarios,
  ehDependente,
  emptyContratoDados,
  onlyDigits,
  validarPlanilha,
  vidasContratadas,
} from '../csModel';

export interface RegistrarContratoInput {
  workspaceId: string;
  empresaId: string;
  saleId?: string | null;
  leadId?: string | null;
  /** Omitido = não mexer nos sete campos. Mandar `emptyContratoDados()` não é
   *  o mesmo: é pedir para APAGAR o que a aba Proposta ou o cliente preencheu. */
  dados?: Partial<ContratoDados> | null;
  /** Quem não conhece a negociação (o Registrar Venda, o formulário externo
   *  do cliente) omite: o contrato que já existe mantém o que tem. */
  modalidade?: ContratoModalidade | null;
  /** Linhas da proposta: definem os documentos e a contagem de vidas.
   *  Omitidas = quem chama não conhece a venda (ficha do cliente, tela de
   *  Empresas): o contrato que já existe mantém vidas, valor e data. Mandar
   *  `[]` não é o mesmo — é dizer que a venda não tem plano. */
  planLines?: ProposalPlanLine[];
  /** Total da venda em reais (o que o modal já calcula). Só vale com `planLines`. */
  valorMensal?: number;
  dataVenda?: string | null;
}

/** Colaborador em que um dependente pode se pendurar: titular, ativo e não
 *  inativado no Hub. `listVidas` já filtra `ativo`, mas a regra não pode
 *  depender do filtro do adapter. */
const ehTitularAtivo = (v: Vida): boolean =>
  !ehDependente(v.tipo) && v.ativo !== false && v.status !== 'inativa';

const toCents = (valor: unknown): number => {
  const n = Number(valor);
  if (!Number.isFinite(n) || n <= 0) return 0;
  // Arredonda em centavos: o catálogo tem preços com centavos (39,90) e
  // somar em float acumula 0,000000001 que vira 1 centavo a menos.
  return Math.round(n * 100);
};

const sanitizeDados = (dados?: Partial<ContratoDados> | null): ContratoDados => {
  const base = emptyContratoDados();
  const t = (v: unknown) => String(v ?? '').trim();
  return {
    ...base,
    ...(dados || {}),
    razaoSocial:       t(dados?.razaoSocial),
    cnpj:              onlyDigits(dados?.cnpj) || '',
    endereco:          t(dados?.endereco),
    email:             t(dados?.email).toLowerCase(),
    representanteNome: t(dados?.representanteNome),
    representanteCpf:  onlyDigits(dados?.representanteCpf) || '',
    emailAssinatura:   t(dados?.emailAssinatura).toLowerCase(),
    representanteQualificacao: qualificacaoOuPadrao(dados?.representanteQualificacao),
    representanteDeclaraPoderes: dados?.representanteDeclaraPoderes === true,
  };
};

export class CsService {
  constructor(private readonly repo: ICsRepository) {}

  // ── Contrato ────────────────────────────────────────────────────

  /**
   * Chamado logo depois do `register_card_sale_from_proposal` ter sucedido.
   * Faz upsert por `sale_id`, então reabrir e ajustar a venda não cria um
   * segundo contrato.
   *
   * Campos em branco são aceitos de propósito: a decisão de produto é que
   * a venda nunca trava por falta de CNPJ. O contrato nasce `pendente` e
   * aparece na fila do CS como incompleto.
   */
  registrarContratoDaVenda = async (input: RegistrarContratoInput): Promise<Contrato | null> => {
    if (!input.workspaceId || !input.empresaId) return null;

    const dados = input.dados ? sanitizeDados(input.dados) : null;

    // A modalidade decide QUAL contrato geral nasce (Blue x convênio) e se
    // existe boleto. Quem chama daqui muitas vezes não a conhece — o
    // Registrar Venda e o formulário que o cliente preenche só sabem dos
    // sete campos. Herdar do contrato que já existe evita que salvar um
    // e-mail transforme um convênio em desconto em folha.
    const existente = (input.saleId ? await this.repo.getContratoBySale(input.saleId) : null)
      || await this.repo.getContratoByEmpresa(input.empresaId);

    const modalidade = input.modalidade || existente?.modalidade || MODALIDADE_PADRAO;
    // O Vida+ não tem mais cobrança própria: vai no boleto sempre que a
    // empresa é desconto em folha. Grava o padrão para a coluna legada não
    // carregar um `cartao` antigo que ninguém consegue mais mudar.
    const vidaMaisCobranca = VIDA_MAIS_COBRANCA_PADRAO;

    // Sem as linhas da venda, os números vêm do contrato que já existe:
    // recalcular de nada zeraria vidas e valor de um contrato fechado.
    const herda = input.planLines === undefined && !!existente;
    const venda = herda && existente
      ? { vidasContratadas: existente.vidasContratadas, valorMensalCents: existente.valorMensalCents, dataVenda: existente.dataVenda }
      : { vidasContratadas: vidasContratadas(input.planLines), valorMensalCents: toCents(input.valorMensal), dataVenda: input.dataVenda || null };

    return this.repo.upsertContrato({
      workspaceId: input.workspaceId,
      empresaId: input.empresaId,
      saleId: input.saleId || null,
      leadId: input.leadId || null,
      dados,
      modalidade,
      vidaMaisCobranca,
      ...venda,
      // Sem as linhas, só o contrato geral: a sincronização cria o que falta
      // e nunca apaga anexo que já existe.
      documentos: documentosNecessarios(input.planLines, modalidade),
    });
  };

  getContratoBySale = (saleId: string): Promise<Contrato | null> =>
    saleId ? this.repo.getContratoBySale(saleId) : Promise.resolve(null);

  getContratoByEmpresa = (empresaId: string): Promise<Contrato | null> =>
    empresaId ? this.repo.getContratoByEmpresa(empresaId) : Promise.resolve(null);

  listDocumentos = (contratoId: string): Promise<ContratoDocumento[]> =>
    contratoId ? this.repo.listDocumentos(contratoId) : Promise.resolve([]);

  listCompetencias = (contratoId: string): Promise<Competencia[]> =>
    contratoId ? this.repo.listCompetencias(contratoId) : Promise.resolve([]);

  criarCompetenciaAtual = (contratoId: string): Promise<Competencia> => {
    if (!contratoId) throw new Error('Contrato obrigatório.');
    return this.repo.criarCompetenciaAtual(contratoId);
  };

  /** Quanto o boleto do mês vai cobrar, sem gravar nada — para a tela pedir
   *  confirmação antes de criar a competência e disparar. */
  previaBoleto = (contratoId: string): Promise<PreviaBoleto> => {
    if (!contratoId) throw new Error('Contrato obrigatório.');
    return this.repo.previaBoleto(contratoId);
  };

  /**
   * Tudo que o modal do boleto precisa, sem gravar nada: a conferência
   * inicial (vidas + venda + catálogo + assinatura), os rascunhos para o aviso e, se a
   * competência do mês já foi preparada no Hub, o preparo — reabrir o modal
   * cai na etapa 2 em vez de abrir vagas de novo.
   */
  dadosDoBoleto = async (contratoId: string, opcoes: { catalogo?: ItemDoCatalogo[]; hoje?: Date } = {}): Promise<{
    conferencia: ConferenciaBoleto;
    rascunhosPendentes: number;
    competencia: Competencia | null;
    preparo: PreparoBoleto | null;
    /** O último envio ao Hub falhou: as vagas podem ter sido abertas. */
    ultimoPreparoFalhou: boolean;
    /** AAAA-MM da competência que o boleto cobra. */
    mesCompetencia: string;
  }> => {
    if (!contratoId) throw new Error('Contrato obrigatório.');
    const hoje = opcoes.hoje ?? new Date();
    const contrato = await this.repo.getContratoById(contratoId);
    if (!contrato) throw new Error('Contrato não encontrado.');
    const [vidas, planosContratados, documentos, previa, competencias] = await Promise.all([
      this.repo.listVidas(contrato.empresaId),
      this.repo.listPlanosContratados(contrato.saleId),
      this.repo.listDocumentos(contratoId),
      this.repo.previaBoleto(contratoId),
      this.repo.listCompetencias(contratoId),
    ]);
    // A competência do mês que ainda não virou boleto. Não cria: abrir o
    // modal e cancelar não pode deixar fatura aberta para trás. O mês é o de
    // `cs.criar_competencia_atual` (`current_date` do banco, em UTC) — com o
    // de Brasília, das 21h do último dia em diante o modal procuraria um mês
    // e a RPC criaria o seguinte.
    const mes = hoje.toISOString().slice(0, 7);
    const competencia = competencias.find(c => c.competencia.startsWith(mes)
      && (c.status === 'aberta' || c.status === 'boleto_solicitado') && !c.boletoUrl) || null;
    const ultimo = competencia
      ? await this.repo.ultimoPreparoBoleto(competencia.id)
      : { preparo: null, ultimoFalhou: false };
    return {
      conferencia: montarConferenciaBoleto({ vidas, planosContratados, documentos, catalogo: opcoes.catalogo }),
      rascunhosPendentes: previa.rascunhosPendentes,
      competencia,
      preparo: ultimo.preparo,
      ultimoPreparoFalhou: ultimo.ultimoFalhou,
      mesCompetencia: mes,
    };
  };

  marcarCompetenciaPaga = (competenciaId: string, paga: boolean): Promise<Competencia> => {
    if (!competenciaId) throw new Error('Boleto obrigatório.');
    return this.repo.marcarCompetenciaPaga(competenciaId, paga);
  };

  // ── Gatilhos ────────────────────────────────────────────────────

  /**
   * Monta o envelope que o n8n espera, lendo o banco — mesma forma do
   * `buildPayload` do servidor, para o workflow não precisar saber por qual
   * caminho a chamada veio.
   *
   * Com `vidaId`, `data.vidas` traz SÓ aquela vida: o ramo de acessos itera
   * sobre a lista, e mandar todas dispararia para a empresa inteira.
   */
  montarPayloadGatilho = async (params: {
    tipo: GatilhoTipo; contratoId: string; requestId: string;
    documentoId?: string | null; documentoTipo?: string | null;
    competenciaId?: string | null; vidaId?: string | null;
  }) => {
    const contrato = await this.repo.getContratoById(params.contratoId);
    if (!contrato) throw new Error('Contrato não encontrado.');

    const [documentos, todasAsVidas, importacoes, planosContratados] = await Promise.all([
      this.repo.listDocumentos(contrato.id),
      this.repo.listVidas(contrato.empresaId),
      this.repo.listImportacoes(contrato.empresaId),
      this.repo.listPlanosContratados(contrato.saleId),
    ]);
    const vidas = params.vidaId ? todasAsVidas.filter(v => v.id === params.vidaId) : todasAsVidas;

    return {
      contrato,
      importacao: importacoes[0] || null,
      payload: {
        event: `cs.${params.tipo}`,
        request_id: params.requestId,
        occurred_at: new Date().toISOString(),
        workspace: { id: contrato.workspaceId },
        empresa: { id: contrato.empresaId, nome: contrato.dados.razaoSocial, cnpj: contrato.dados.cnpj },
        contrato: {
          id: contrato.id,
          razao_social: contrato.dados.razaoSocial,
          cnpj: contrato.dados.cnpj,
          endereco: contrato.dados.endereco,
          email: contrato.dados.email,
          representante_nome: contrato.dados.representanteNome,
          representante_cpf: contrato.dados.representanteCpf,
          email_assinatura: contrato.dados.emailAssinatura,
          // O Hub recusa o contrato (422) sem as duas: vão no
          // `representante_legal` como `qualificacao` e `declara_poderes`.
          representante_qualificacao: qualificacaoOuPadrao(contrato.dados.representanteQualificacao),
          // O cargo é o mesmo select, por extenso: "Administrador".
          representante_cargo: QUALIFICACAO_LABELS[qualificacaoOuPadrao(contrato.dados.representanteQualificacao)],
          representante_declara_poderes: contrato.dados.representanteDeclaraPoderes,
          // O fluxo de contrato escolhe o template por aqui: convênio e
          // desconto em folha são peças diferentes. `documento_tipo` já diz o
          // mesmo, mas o boleto e o envio de vidas também precisam saber quem
          // paga o quê.
          modalidade: contrato.modalidade,
          vida_mais_cobranca: contrato.vidaMaisCobranca,
          vidas_contratadas: contrato.vidasContratadas,
          valor_mensal_cents: contrato.valorMensalCents,
        },
        data: {
          documento_id: params.documentoId || null,
          documento_tipo: params.documentoTipo || null,
          competencia_id: params.competenciaId || null,
          vida_id: params.vidaId || null,
          importacao: importacoes[0] || null,
          documentos,
          vidas,
        },
        resumo_planos: {
          quantidade_planos: planosContratados.filter(p => !p.adicional).reduce((total, p) => total + p.quantidade, 0),
          // `valor_unitario` é o preço que vale; `valor_tabela` e `meses` só
          // existem quando houve negociação, e vão junto para o contrato poder
          // escrever a condição que o cliente aceitou ("R$ 29 por 6 meses").
          planos: planosContratados.filter(p => !p.adicional).map(p => ({
            nome: p.nome, quantidade: p.quantidade, valor_unitario: p.valorUnitario, valor_total: p.valorTotal,
            valor_tabela: p.valorTabela, meses: p.meses,
          })),
          adicionais: planosContratados.filter(p => p.adicional).map(p => ({
            nome: p.nome, quantidade: p.quantidade, valor_unitario: p.valorUnitario, valor_total: p.valorTotal,
            valor_tabela: p.valorTabela, meses: p.meses,
          })),
          valor_total: planosContratados.reduce((total, p) => total + p.valorTotal, 0),
          valor_mensal: contrato.valorMensalCents / 100,
        },
      },
    };
  };

  registrarGatilho = (input: RegistrarGatilhoInput) => this.repo.registrarGatilho(input);

  gatilhoJaTeveSucesso = (empresaId: string, tipo: GatilhoTipo, dedupeKey: string) =>
    this.repo.gatilhoJaTeveSucesso(empresaId, tipo, dedupeKey);

  // ── Vida individual ─────────────────────────────────────────────
  // Passa pela MESMA validação da planilha: uma vida digitada na tela não
  // pode entrar com CPF inválido ou 71 anos só porque não veio de um CSV.
  private validarVida = (
    entrada: {
      nome: string; cpf: string; dataNascimento: string; email: string;
      telefone?: string | null; plano: string;
      tipo?: VidaTipo; titularCpf?: string | null;
      odonto?: boolean; psicologo?: boolean; nomeMae?: string | null;
    },
    planos: PlanoDoCatalogo[],
    hoje: Date,
    jaExistentes: { cpfs: string[]; emails: string[]; cpfsColaboradores: string[] },
  ) => {
    const validada = validarPlanilha([{
      numero: 1,
      tipo: entrada.tipo || 'colaborador',
      nome: entrada.nome,
      cpf: entrada.cpf,
      dataNascimento: entrada.dataNascimento,
      email: entrada.email,
      telefone: entrada.telefone || '',
      plano: entrada.plano,
      titularCpf: entrada.titularCpf || '',
      odonto: entrada.odonto ? 'Sim' : 'Não',
      psicologo: entrada.psicologo ? 'Sim' : 'Não',
      nomeMae: entrada.nomeMae || '',
    }], {
      planos, hoje,
      cpfsExistentes: jaExistentes.cpfs,
      emailsExistentes: jaExistentes.emails,
      cpfsColaboradoresExistentes: jaExistentes.cpfsColaboradores,
    });

    if (validada.erros.length) throw new Error(validada.erros.map(e => e.motivo).join('; '));
    const vida = validada.vidas[0];
    if (!vida) throw new Error('Linha vazia — preencha os dados da vida.');
    return vida;
  };

  /** O titular de um dependente (Vida+ ou só adicional) tem que ser um
   *  colaborador ATIVO desta empresa — o banco recusa qualquer outra
   *  coisa, e a mensagem de lá não explica. */
  private acharTitular = async (empresaId: string, titularId?: string | null): Promise<Vida> => {
    if (!titularId) throw new Error('Escolha o colaborador titular do dependente.');
    const titular = (await this.repo.listVidas(empresaId))
      .find(v => v.id === titularId && ehTitularAtivo(v));
    if (!titular) throw new Error('Colaborador titular não encontrado nesta empresa.');
    return titular;
  };

  criarVida = async (params: {
    workspaceId: string; empresaId: string; contratoId?: string | null;
    tipo?: VidaTipo; titularId?: string | null;
    nome: string; cpf: string; dataNascimento: string; email: string;
    telefone?: string | null; plano: string;
    odonto?: boolean; psicologo?: boolean; nomeMae?: string | null;
    planos: PlanoDoCatalogo[]; hoje?: Date;
  }): Promise<Vida> => {
    const tipo: VidaTipo = params.tipo || 'colaborador';
    const existentes = await this.repo.getIdentificadoresDaEmpresa(params.empresaId);
    const titular = ehDependente(tipo)
      ? await this.acharTitular(params.empresaId, params.titularId)
      : null;

    const vida = this.validarVida(
      { ...params, tipo, titularCpf: titular?.cpf || null },
      params.planos, params.hoje ?? new Date(), existentes,
    );

    return this.repo.criarVida({
      workspaceId: params.workspaceId,
      empresaId: params.empresaId,
      contratoId: params.contratoId || null,
      // Usa o tipo que o NÚCLEO decidiu, não o que a tela mandou: um Vida+
      // com "Nenhum Plano contratado" vira só adicional ali dentro, e o
      // banco tem que gravar o que de fato é, não o que foi pedido.
      tipo: vida.tipo,
      titularId: titular?.id || null,
      nome: vida.nome,
      cpf: vida.cpf,
      dataNascimento: vida.dataNascimento,
      email: vida.email,
      telefone: vida.telefone || null,
      planoId: vida.planoId || null,
      planoNomeRaw: vida.planoNomeRaw,
      odonto: vida.odonto,
      psicologo: vida.psicologo,
      nomeMae: vida.nomeMae,
    });
  };

  atualizarVida = async (params: {
    vidaId: string; empresaId: string;
    nome: string; cpf: string; dataNascimento: string; email: string;
    telefone?: string | null; plano: string;
    /** Só dependente pode mandar: é o que permite trocar entre Vida+ e só
     *  adicional na edição. Colaborador não vira dependente por aqui, nem
     *  o contrário. */
    tipo?: VidaTipo;
    odonto?: boolean; psicologo?: boolean; nomeMae?: string | null;
    planos: PlanoDoCatalogo[]; hoje?: Date;
  }): Promise<Vida> => {
    // A própria vida não pode colidir consigo mesma na checagem de duplicidade.
    const atuais = await this.repo.listVidas(params.empresaId);
    const atual = atuais.find(v => v.id === params.vidaId);
    const outras = atuais.filter(v => v.id !== params.vidaId);
    const dependenteAtual = !!atual && ehDependente(atual.tipo);

    // O vínculo não se edita aqui: trocar o titular de um dependente é outra
    // operação, e a edição de campos não pode virar um vínculo desfeito.
    const titularCpf = dependenteAtual
      ? (atuais.find(v => v.id === atual!.titularId)?.cpf || null)
      : null;

    // Tipo pedido: só o dependente pode trocar (Vida+ ↔ só adicional), e só
    // para o que ele já é — colaborador continua colaborador por aqui.
    const tipoPedido: VidaTipo = dependenteAtual
      ? (params.tipo && ehDependente(params.tipo) ? params.tipo : atual!.tipo)
      : (atual?.tipo || 'colaborador');

    const vida = this.validarVida(
      { ...params, tipo: tipoPedido, titularCpf },
      params.planos, params.hoje ?? new Date(),
      {
        cpfs: outras.map(v => v.cpf),
        emails: outras.map(v => v.email),
        cpfsColaboradores: outras.filter(v => !ehDependente(v.tipo)).map(v => v.cpf)
          .concat(titularCpf ? [titularCpf] : []),
      },
    );

    return this.repo.atualizarVida(params.vidaId, {
      tipo: vida.tipo,
      nome: vida.nome,
      cpf: vida.cpf,
      dataNascimento: vida.dataNascimento,
      email: vida.email,
      telefone: vida.telefone || null,
      planoId: vida.planoId || null,
      planoNomeRaw: vida.planoNomeRaw,
      odonto: vida.odonto,
      psicologo: vida.psicologo,
      nomeMae: vida.nomeMae,
    });
  };

  /**
   * Vida `pendente` nunca saiu do CRM: pode sumir de verdade. Depois de
   * enviada ao hub ela vira INATIVA — apagar deixaria o hub com um seat que
   * o CRM não sabe explicar.
   */
  removerVida = async (vida: Vida): Promise<void> => {
    if (vida.status === 'pendente') return this.repo.removerVida(vida.id);
    await this.repo.atualizarVida(vida.id, { status: 'inativa', ativo: false });
  };

  /** Conferência manual da assinatura, enquanto o retorno da D4Sign não
   *  chega ao CRM por webhook. */
  marcarDocumento = (documentoId: string, status: DocumentoStatus): Promise<ContratoDocumento> => {
    if (!documentoId) return Promise.reject(new Error('Documento obrigatório.'));
    return this.repo.setDocumentoStatus(documentoId, status);
  };

  /** Quais campos ainda faltam — a tela mostra a lista, não um "inválido".
   *  A parceira não assina, então os de assinatura não contam para ela. */
  camposFaltando = (dados?: Partial<ContratoDados> | null, modalidade?: ContratoModalidade) =>
    contratoCamposFaltando(sanitizeDados(dados), modalidade);

  // ── Planilha de vidas ───────────────────────────────────────────

  /**
   * Valida as linhas já mapeadas pelo parser contra o catálogo e contra o
   * que a empresa já tem. Não grava nada — é o passo do preview, para o
   * comercial corrigir a planilha antes de enviar.
   */
  validarPlanilhaDaEmpresa = async (params: {
    empresaId: string;
    linhas: PlanilhaLinha[];
    planos: PlanoDoCatalogo[];
    hoje: Date;
  }): Promise<PlanilhaValidada> => {
    const validar = await this.prepararValidacaoDaEmpresa(params);
    return validar(params.linhas);
  };

  /**
   * Busca o que a empresa já tem UMA vez e devolve o validador pronto. A
   * correção na tela revalida a planilha inteira a cada linha corrigida —
   * duplicidade é entre linhas — e não precisa ir ao banco de novo para isso.
   */
  prepararValidacaoDaEmpresa = async (params: {
    empresaId: string;
    planos: PlanoDoCatalogo[];
    hoje: Date;
  }): Promise<(linhas: PlanilhaLinha[]) => PlanilhaValidada> => {
    const { cpfs, emails, cpfsColaboradores } = params.empresaId
      ? await this.repo.getIdentificadoresDaEmpresa(params.empresaId)
      : { cpfs: [], emails: [], cpfsColaboradores: [] };

    return (linhas) => validarPlanilha(linhas, {
      planos: params.planos,
      cpfsExistentes: cpfs,
      emailsExistentes: emails,
      // Uma planilha só de Vida+ é caso normal: o RH manda os dependentes
      // depois dos titulares já cadastrados.
      cpfsColaboradoresExistentes: cpfsColaboradores,
      hoje: params.hoje,
    });
  };

  /**
   * Grava a importação e as vidas válidas.
   *
   * Linhas com erro NÃO bloqueiam as boas: o lote entra como `com_erros`,
   * as válidas viram vida e os erros ficam registrados em
   * `cs.vidas_importacoes.erros`. O oposto — recusar as 40 boas por causa
   * de 2 ruins — obrigaria o comercial a refazer a planilha inteira.
   */
  importarVidas = async (params: {
    workspaceId: string;
    empresaId: string;
    contratoId?: string | null;
    saleId?: string | null;
    validacao: PlanilhaValidada;
    arquivo?: CriarImportacaoInput['arquivo'];
    negociacao?: NegociacaoPlanilha | null;
    /** Saída de `montarLoteDaImportacao`: as linhas com erro de `validacao`
     *  (D1) e as que foram mandadas para rascunho na prévia. */
    rascunhos?: LinhaParaRascunho[];
    /** Linhas tiradas na prévia. Só a contagem vai ao banco. */
    totalDescartadas?: number;
  }): Promise<Importacao | null> => {
    if (!params.workspaceId || !params.empresaId) return null;
    const { validacao } = params;
    const rascunhos = params.rascunhos || [];
    const totalDescartadas = Math.max(0, Math.floor(Number(params.totalDescartadas) || 0));
    if (!validacao.vidas.length && !validacao.erros.length && !rascunhos.length) return null;

    // `totalLinhas` é o tamanho útil do arquivo: a validação só viu as linhas
    // que seguiram na prévia. As que foram para rascunho por escolha e as
    // descartadas somam por fora; as de rascunho por erro (D1) já estão nela.
    const linhasComErro = new Set(validacao.erros.map(chaveDoErro));
    const rascunhosForaDaValidacao = rascunhos.filter(r => !linhasComErro.has(chaveDaLinha(r.linha))).length;
    // Invariante: uma linha nunca é vida E rascunho. Se quem chama mandou a
    // validação crua em vez do lote, o rascunho vence — vida a mais é cobrança.
    const emRascunho = new Set(rascunhos.map(r => chaveDaLinha(r.linha)));
    const vidas = validacao.vidas.filter(v => !emRascunho.has(chaveDaLinha({ aba: v.aba, numero: v.linha })));

    return this.repo.criarImportacao({
      workspaceId: params.workspaceId,
      empresaId: params.empresaId,
      contratoId: params.contratoId || null,
      saleId: params.saleId || null,
      arquivo: params.arquivo || null,
      totalLinhas: validacao.totalLinhas + rascunhosForaDaValidacao + totalDescartadas,
      totalValidas: vidas.length,
      totalErros: validacao.totalErros,
      erros: validacao.erros,
      negociacao: params.negociacao || null,
      vidas,
      totalDescartadas,
      rascunhos,
    });
  };

  listVidas = (empresaId: string): Promise<Vida[]> =>
    empresaId ? this.repo.listVidas(empresaId) : Promise.resolve([]);

  listImportacoes = (empresaId: string): Promise<Importacao[]> =>
    empresaId ? this.repo.listImportacoes(empresaId) : Promise.resolve([]);

  listHistoricoImportacoes = (empresaId: string): Promise<ImportacaoHistorico[]> =>
    empresaId ? this.repo.listHistoricoImportacoes(empresaId) : Promise.resolve([]);

  // ── Rascunhos de vidas ──────────────────────────────────────────
  // pendente → resolvido | descartado. Quem garante a transição atômica é o
  // banco (cs.resolver_rascunho_vida / cs.descartar_rascunho_vida, com FOR
  // UPDATE exigindo pendente); aqui fica a validação da linha, que é a mesma
  // da planilha. Limite de vidas contratadas não entra em nada disto (D2).

  listRascunhos = (empresaId: string): Promise<VidaRascunho[]> =>
    empresaId ? this.repo.listRascunhos(empresaId) : Promise.resolve([]);

  /** O rascunho pendente desta empresa, e os demais pendentes — é entre eles
   *  que um dependente procura o titular que ainda não virou vida. */
  private rascunhoPendente = async (empresaId: string, rascunhoId: string) => {
    if (!rascunhoId || !empresaId) throw new Error('Rascunho obrigatório.');
    const pendentes = await this.repo.listRascunhos(empresaId);
    const rascunho = pendentes.find(r => r.id === rascunhoId);
    if (!rascunho) throw new Error('Rascunho já resolvido ou descartado.');
    return { rascunho, outros: pendentes.filter(r => r.id !== rascunhoId) };
  };

  private validarRascunho = async (p: {
    rascunhoId: string; empresaId: string; linha: PlanilhaLinha;
    planos: PlanoDoCatalogo[]; hoje?: Date;
  }) => {
    const { rascunho, outros } = await this.rascunhoPendente(p.empresaId, p.rascunhoId);
    const existentes = await this.repo.getIdentificadoresDaEmpresa(p.empresaId);
    // Aba e número são o endereço da linha no arquivo: a edição não os muda.
    const linha: PlanilhaLinha = { ...p.linha, aba: rascunho.linha.aba, numero: rascunho.linha.numero };
    const r = validarLinhaDoRascunho(linha, {
      planos: p.planos,
      hoje: p.hoje ?? new Date(),
      cpfsExistentes: existentes.cpfs,
      emailsExistentes: existentes.emails,
      cpfsColaboradoresExistentes: existentes.cpfsColaboradores,
    }, outros.map(o => o.linha));
    return { rascunho, linha, ...r };
  };

  /** Salva o ajuste parcial (D4): grava a linha e os erros que ela ainda
   *  tem, sem virar vida. Com erro também — é o "Salvar rascunho". */
  salvarRascunho = async (p: {
    rascunhoId: string; empresaId: string; linha: PlanilhaLinha;
    planos: PlanoDoCatalogo[]; hoje?: Date;
  }): Promise<VidaRascunho> => {
    const { linha, erros } = await this.validarRascunho(p);
    return this.repo.salvarRascunho(p.rascunhoId, linha, erros);
  };

  /** "Salvar e cadastrar": só com a linha sem erro nenhum. Lança com o motivo
   *  legível; o rascunho continua pendente e intacto. */
  resolverRascunho = async (p: {
    rascunhoId: string; empresaId: string; contratoId?: string | null;
    linha: PlanilhaLinha; planos: PlanoDoCatalogo[]; hoje?: Date;
  }): Promise<Vida> => {
    const { rascunho, vida, erros } = await this.validarRascunho(p);
    if (erros.length || !vida) {
      throw new Error(erros.map(e => e.motivo).join('; ') || 'Linha vazia — preencha os dados da vida.');
    }

    let titularId: string | null = null;
    if (ehDependente(vida.tipo)) {
      // A validação usou os CPFs de colaborador ativo; aqui se acha o id. Se
      // sumiu no meio-tempo (inativado), a mensagem é a mesma da validação.
      const titular = (await this.repo.listVidas(p.empresaId))
        .find(v => ehTitularAtivo(v) && onlyDigits(v.cpf) === vida.titularCpf);
      if (!titular) throw new Error('Colaborador titular não cadastrado: corrija o CPF do titular ou descarte');
      titularId = titular.id;
    }

    const input: CriarVidaInput = {
      workspaceId: rascunho.workspaceId,
      empresaId: rascunho.empresaId,
      contratoId: p.contratoId || null,
      tipo: vida.tipo,
      titularId,
      nome: vida.nome,
      cpf: vida.cpf,
      dataNascimento: vida.dataNascimento,
      email: vida.email,
      telefone: vida.telefone || null,
      planoId: vida.planoId || null,
      planoNomeRaw: vida.planoNomeRaw,
      odonto: vida.odonto,
      psicologo: vida.psicologo,
      nomeMae: vida.nomeMae,
    };
    return this.repo.resolverRascunho(p.rascunhoId, input);
  };

  /** Apaga os dados pessoais (LGPD) e guarda só aba, número e tipo. */
  descartarRascunho = (rascunhoId: string): Promise<VidaRascunho> => {
    if (!rascunhoId) return Promise.reject(new Error('Rascunho obrigatório.'));
    return this.repo.descartarRascunho(rascunhoId);
  };

  /**
   * A planilha bate com o que foi vendido?
   *
   * Divergência é AVISO, nunca bloqueio: a própria doc do hub descreve o
   * caso normal de a empresa pagar por N licenças e o RH preencher os
   * colaboradores ao longo da semana.
   */
  conferirContagem = (params: {
    vidasNaPlanilha: number;
    planLines?: ProposalPlanLine[];
    /** Quando quem chama já tem o número (o contrato guarda), evita ter de
     *  carregar as linhas da proposta só para recontá-las. */
    contratadas?: number;
  }): { contratadas: number; naPlanilha: number; diferenca: number; aviso: string | null } => {
    const contratadas = params.contratadas ?? vidasContratadas(params.planLines);
    const naPlanilha = Math.max(0, params.vidasNaPlanilha);
    const diferenca = naPlanilha - contratadas;

    let aviso: string | null = null;
    if (contratadas > 0 && diferenca < 0) {
      aviso = `A planilha tem ${naPlanilha} vida(s) e a venda contratou ${contratadas}. `
        + 'As vagas restantes ficam abertas até o RH preencher.';
    } else if (contratadas > 0 && diferenca > 0) {
      aviso = `A planilha tem ${naPlanilha} vida(s), ${diferenca} a mais que as ${contratadas} `
        + 'contratadas na venda. Confira antes de enviar ao hub.';
    }
    return { contratadas, naPlanilha, diferenca, aviso };
  };

  // ── Área de Implantação ─────────────────────────────────────────
  // Passagem fina: quem autoriza e quem filtra é o banco (as RPCs
  // `cs.listar_clientes_implantacao` e `cs.obter_cliente_implantacao`).
  // Repetir a regra aqui criaria uma segunda fonte para a mesma decisão —
  // e seria a que não vale.

  currentUserIsImplantacao = (): Promise<boolean> =>
    this.repo.currentUserIsImplantacao();

  listClientesImplantacao = (): Promise<ClienteImplantacao[]> =>
    this.repo.listClientesImplantacao();

  getClienteImplantacao = (empresaId: string): Promise<ClienteImplantacaoResultado> =>
    empresaId
      ? this.repo.getClienteImplantacao(empresaId)
      : Promise.resolve({ estado: 'inexistente' as const });

  // ── Ficha do cliente (/clientes/:empresaId) ────────────────────
  // Uma rota, dois públicos: o adapter já devolve o estado traduzido por
  // `estadoDaFicha` (csModel) — aqui só cobre o caso de empresaId vazio.

  obterFichaCliente = (empresaId: string): Promise<EstadoFicha> =>
    empresaId
      ? this.repo.obterFichaCliente(empresaId)
      : Promise.resolve({ estado: 'nao_encontrado' as const });

  listGatilhosEmpresa = (empresaId: string): Promise<GatilhoEmpresa[]> =>
    empresaId ? this.repo.listGatilhosEmpresa(empresaId) : Promise.resolve([]);

  /** Clientes Ativos: empresas com contrato enviado, com os status. */
  listCarteiraDeContratos = async () => montarCarteira(await this.repo.listContratosDaCarteira());

  /** Super Admin: some com a empresa de teste dos Clientes Ativos. */
  ocultarEmpresaDaCarteira = (empresaId: string): Promise<number> =>
    this.repo.ocultarEmpresaDaCarteira(empresaId);

  // ── Rota de integração ──────────────────────────────────────────
  // A tela lê para travar o que o Hub não aceita; quem recusa o disparo é o
  // servidor (server/cs-automation.js), com a mesma resolução.

  getRotaIntegracao = (empresaId: string): Promise<RotaIntegracao> =>
    empresaId
      ? this.repo.getRotaIntegracao(empresaId)
      : Promise.resolve({ contratoVia: 'n8n' as const, cobrancaVia: 'n8n' as const });

  getRotaGlobal = (): Promise<RotaVia> => this.repo.getRotaGlobal();

  salvarRotaGlobal = (via: RotaVia): Promise<void> => this.repo.salvarRotaGlobal(via);

  buscarEmpresasParaRota = (workspaceId: string, query: string): Promise<EmpresaParaRota[]> =>
    workspaceId ? this.repo.buscarEmpresasParaRota(workspaceId, query) : Promise.resolve([]);

  // ── Envios desativados ──────────────────────────────────────────
  // Mesmo desenho da rota: a tela lê para travar o botão, o servidor recusa.

  listEnviosBloqueados = (empresaId: string): Promise<EnvioBloqueado[]> =>
    empresaId ? this.repo.listEnviosBloqueados(empresaId) : Promise.resolve([]);

  listEnviosBloqueadosAdmin = (): Promise<EnvioBloqueadoRegistro[]> =>
    this.repo.listEnviosBloqueadosAdmin();

  bloquearEnvio = (workspaceId: string, empresaId: string, tipo: GatilhoTipo, motivo: string): Promise<void> => {
    const texto = motivo.trim();
    if (!texto) return Promise.reject(new Error('Diga o motivo: é o que a equipe vai ler no botão travado.'));
    return this.repo.bloquearEnvio(workspaceId, empresaId, tipo, texto);
  };

  desbloquearEnvio = (id: string): Promise<void> =>
    this.repo.desbloquearEnvio(id);

  // ── Clientes do Hub (/clientes, aba Hub) ────────────────────────
  // Passagem fina, como a Área de Implantação: quem autoriza e quem monta
  // o vínculo sugerido é o banco (`cs.listar_clientes_hub` e as RPCs de
  // vínculo). Repetir a regra aqui criaria uma segunda fonte da decisão.

  podeVerClientesHub = (): Promise<boolean> =>
    this.repo.podeVerClientesHub();

  podeVincularEmpresaHub = (): Promise<boolean> =>
    this.repo.podeVincularEmpresaHub();

  listClientesHub = (): Promise<ClienteHub[]> =>
    this.repo.listClientesHub();

  ultimaSincronizacaoHub = (): Promise<UltimaSincronizacaoHub | null> =>
    this.repo.ultimaSincronizacaoHub();

  vincularEmpresaHub = (
    hubPublicId: string,
    empresaId: string,
    como: 'matriz' | 'filial' = 'matriz',
  ): Promise<void> => {
    if (!hubPublicId || !empresaId) return Promise.reject(new Error('Empresa do Hub e empresa do CRM são obrigatórias.'));
    return this.repo.vincularEmpresaHub(hubPublicId, empresaId, como);
  };

  desvincularEmpresaHub = (hubPublicId: string): Promise<void> =>
    this.repo.desvincularEmpresaHub(hubPublicId);

  /** Busca manual (campo da `VincularDialog`) — só dispara com termo
   *  de verdade; o mínimo de caracteres é regra da tela (evitar buscar a
   *  cada tecla), não do banco. */
  buscarEmpresasParaVinculo = (termo: string, marca: string): Promise<EmpresaParaVinculo[]> =>
    (termo.trim().length >= 2 && marca)
      ? this.repo.buscarEmpresasParaVinculo(termo.trim(), marca)
      : Promise.resolve([]);

  listContasHubDaEmpresa = (empresaId: string): Promise<ContaHubDaEmpresa[]> =>
    empresaId ? this.repo.listContasHubDaEmpresa(empresaId) : Promise.resolve([]);

  empresaTravadaPeloHub = (empresaId: string): Promise<boolean> =>
    empresaId ? this.repo.empresaTravadaPeloHub(empresaId) : Promise.resolve(false);
}
